// The conversation as assistant-ui's messages (design-revamp.md section 5, Grouping). Pure, so it is
// tested without a browser: a user item is a user message; the assistant and tool items of one turn,
// and of the turns of its chain, are one assistant message, its parts in seq order; the running
// turn's live text is its last part; a check-in is a system divider; the pending message is the
// person's last message until its item arrives. Each message carries when it was written, and the
// time divider that goes before it, in `metadata.custom`. A user message's files are its attachments,
// each by its Drive path, with their sizes in `metadata.custom.attachments` (attachments.md section 8).
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { AttachmentRef } from "./attachments.js";
import { checkIn, isRunning, timeline, type Chat, type Item } from "./chat.js";
import { isAlphaLabUrl, shownUrl } from "./links.js";
import { alphalabOrigin } from "./theme.js";
import { clockTime, dividerLabel, needsDivider, type Zone } from "./times.js";

/** What a tool card reads from a tool-call part (`artifact`). */
export interface ToolCardData {
  status: "running" | "completed" | "failed";
  args?: string;
  result?: string;
  links: string[];
}

type Part = Exclude<ThreadMessageLike["content"], string>[number];

interface Draft {
  id: string;
  role: "user" | "assistant" | "system";
  turnIds: Set<string>;
  parts: Part[];
  custom?: Record<string, unknown>;
  /** When its first and last items were written; unset when none has a time. */
  at?: number;
  lastAt?: number;
  /** A check-in: its text is made once its divider is known. */
  scheduled?: ReturnType<typeof checkIn>;
  /** A user message's files. */
  attachments?: AttachmentRef[];
}

/** A user message's parts: its text, none when it carries only files. */
function userParts(text: string, attachments: AttachmentRef[] | undefined): Part[] {
  return text || !attachments?.length ? [{ type: "text", text }] : [];
}

/** A file on a sent message as assistant-ui holds it: its Drive path is its id; nothing of its bytes. */
function sentAttachment(a: AttachmentRef): NonNullable<ThreadMessageLike["attachments"]>[number] {
  return { id: a.path, type: a.contentType.startsWith("image/") ? "image" : "document", name: a.name, contentType: a.contentType, status: { type: "complete" }, content: [] };
}

/** What a message reads from `metadata.custom` to show its time. */
export interface MessageTime {
  /** When it was written, epoch ms. */
  at?: number;
  /** The time divider before it, e.g. "Today 3:52 PM". */
  divider?: string;
}

function stamp(d: Draft, at: number | undefined): void {
  // Items stored before they had a time read back as 0.
  if (!at) return;
  d.at ??= at;
  d.lastAt = Math.max(d.lastAt ?? at, at);
}

function toolPart(item: Item, origin: string | undefined): Part {
  // The panel tool's own URL is its link, for the card on a narrow screen (section 8), but only for
  // a call that completed. Every link shown is on the environment's AlphaLab app, never elsewhere.
  const shown = item.tool === "quanty_show_in_alphalab" && item.status === "completed" ? shownUrl(item.args)?.url : undefined;
  // Links stored before the API read them unescaped can end in a backslash, and repeat without one.
  const stored = (item.links ?? []).map((url) => url.replace(/\\+$/, ""));
  const links = [...new Set([...stored, ...(shown ? [shown] : [])])].filter((url) => isAlphaLabUrl(url, origin));
  const card: ToolCardData = { status: item.status ?? "completed", args: item.args, result: item.result, links };
  return {
    type: "tool-call",
    toolCallId: `t${item.seq}`,
    toolName: item.tool ?? "tool",
    argsText: item.args ?? "",
    artifact: card,
    // A result marks the call finished; a running call has none.
    ...(card.status === "running" ? {} : { result: item.result ?? "", isError: card.status === "failed" }),
  } as Part;
}

export function threadOf(chat: Chat, origin: string | undefined = alphalabOrigin(), now: number = Date.now(), zone: Zone = {}): ThreadMessageLike[] {
  // The turns a chain continued into: their items join the message of the turn before.
  const continued = new Set(chat.items.filter((i) => i.kind === "user" && i.continuation).map((i) => i.turnId));
  const drafts: Draft[] = [];
  const assistantFor = (turnId: string, seq: number): Draft => {
    const last = drafts.at(-1);
    if (last?.role === "assistant" && (last.turnIds.has(turnId) || continued.has(turnId))) {
      last.turnIds.add(turnId);
      return last;
    }
    const d: Draft = { id: `a${seq}`, role: "assistant", turnIds: new Set([turnId]), parts: [] };
    drafts.push(d);
    return d;
  };

  for (const item of timeline(chat)) {
    if (item.kind === "user") {
      const scheduled = checkIn(item);
      if (scheduled) {
        drafts.push({ id: `s${item.seq}`, role: "system", turnIds: new Set([item.turnId]), parts: [], scheduled });
      } else {
        drafts.push({
          id: `u${item.seq}`,
          role: "user",
          turnIds: new Set([item.turnId]),
          parts: userParts(item.text ?? "", item.attachments),
          ...(item.attachments?.length ? { attachments: item.attachments } : {}),
        });
      }
      stamp(drafts.at(-1)!, item.createdAt);
      continue;
    }
    const d = assistantFor(item.turnId, item.seq);
    stamp(d, item.createdAt);
    if (item.kind === "tool") d.parts.push(toolPart(item, origin));
    else if (item.text?.trim()) d.parts.push({ type: "text", text: item.text });
  }

  if (chat.live?.text) {
    // Live text is being written now; the pending message was written when Send was pressed.
    const d = assistantFor(chat.live.turnId, Number.MAX_SAFE_INTEGER);
    d.parts.push({ type: "text", text: chat.live.text });
    stamp(d, now);
  }

  if (chat.pending) {
    drafts.push({
      id: `p${chat.pending.key}`,
      role: "user",
      turnIds: new Set(chat.pending.turnId ? [chat.pending.turnId] : []),
      parts: userParts(chat.pending.text, chat.pending.attachments),
      ...(chat.pending.attachments?.length ? { attachments: chat.pending.attachments } : {}),
      custom: { pending: chat.pending.state, reason: chat.pending.reason },
      at: chat.pending.sentAt ?? now,
      lastAt: chat.pending.sentAt ?? now,
    });
  }

  const runningTurn = isRunning(chat.turn) ? chat.turn!.turnId : undefined;
  const shown = drafts.filter((d) => d.role !== "assistant" || d.parts.length > 0);
  // A divider before the first message with a time, and after a gap of 30 minutes or a new day.
  let previous: number | undefined;
  for (const d of shown) {
    let divider = d.at !== undefined && needsDivider(previous, d.at, zone) ? dividerLabel(d.at, now, zone) : undefined;
    if (d.scheduled) {
      // A check-in's line shows its time, and in place of a divider, its day too.
      const { what, at, time, kind } = d.scheduled;
      const when = time === undefined ? at : divider ? dividerLabel(time, now, zone) : clockTime(time, zone);
      d.parts = [{ type: "text", text: `${kind === "task" ? "Task turn" : "Scheduled"} · ${what} · ${when}` }];
      if (time !== undefined) divider = undefined;
    }
    if (d.at === undefined) continue;
    const stamped: MessageTime = { at: d.at, ...(divider ? { divider } : {}) };
    d.custom = { ...d.custom, ...stamped };
    previous = d.lastAt;
  }
  return shown.map((d): ThreadMessageLike => {
    const custom = d.attachments ? { ...d.custom, attachments: d.attachments } : d.custom;
    const metadata = custom ? { metadata: { custom } } : {};
    if (d.role === "assistant") {
      const running = runningTurn !== undefined && d.turnIds.has(runningTurn);
      return { id: d.id, role: "assistant", content: d.parts, status: running ? { type: "running" } : { type: "complete", reason: "stop" }, ...metadata };
    }
    return { id: d.id, role: d.role, content: d.parts, ...(d.attachments ? { attachments: d.attachments.map(sentAttachment) } : {}), ...metadata };
  });
}

/** Whether the screen should say Quanty One is thinking: work is on, and nothing of it is shown yet. */
export function thinking(chat: Chat): boolean {
  if (chat.live?.text) return false;
  if (chat.pending && chat.pending.state !== "accepted" && chat.pending.state !== "sending") return false;
  if (!chat.pending && !isRunning(chat.turn)) return false;
  // Something of the running turn already shown (a tool card) is the sign of work.
  const last = threadOf(chat).at(-1);
  return !(last?.role === "assistant" && last.status?.type === "running");
}
